import { useMemo } from 'react';
import { canSplitBlock, getNSplitOptionsForBlock } from '../../apps/cidr-planner/splitPlanner';
import { isAwsSubnetAllowed } from '../../apps/cidr-planner/usableIpUtils';
import type { CidrBlock, IpCountMode } from '../../apps/cidr-planner/types';
import type { DisplayLang } from '../../../shared/utils/pageDisplay';
import { CidrSheet } from './CidrSheet';

type BlockActionDialogProps = {
  block: CidrBlock;
  existingCidrs: string[];
  ipCountMode: IpCountMode;
  lang: DisplayLang;
  onClose: () => void;
  onEdit: () => void;
  onSplit: () => void;
};

const TEXT = {
  ja: {
    title: 'ブロック',
    hint: '詳細を編集するか、等分に分割できます。',
    cidr: (cidr: string) => `対象: ${cidr}`,
    edit: '詳細を編集',
    split: '等分分割',
    splitBlocked:
      '重なる別ブロックがあるため分割できません。先に重なりを解消してください。',
    awsNoSplit: 'AWS VPC では /28 より細かく分割できません',
    close: '閉じる',
  },
  en: {
    title: 'Block',
    hint: 'Edit details or split the block into equal parts.',
    cidr: (cidr: string) => `Target: ${cidr}`,
    edit: 'Edit details',
    split: 'Split equally',
    splitBlocked: 'Cannot split while another block overlaps this one.',
    awsNoSplit: 'AWS VPC cannot split smaller than /28',
    close: 'Close',
  },
} as const;

export function BlockActionDialog({
  block,
  existingCidrs,
  ipCountMode,
  lang,
  onClose,
  onEdit,
  onSplit,
}: BlockActionDialogProps) {
  const t = TEXT[lang];
  const splitAllowed = useMemo(
    () => canSplitBlock(block.cidr, existingCidrs),
    [block.cidr, existingCidrs],
  );
  const splitOptions = useMemo(() => {
    const all = getNSplitOptionsForBlock(block.cidr);
    return all.filter((option) => isAwsSubnetAllowed(option.prefix, ipCountMode));
  }, [block.cidr, ipCountMode]);
  const canSplit = splitAllowed.ok && splitOptions.length > 0;
  const splitHint = !splitAllowed.ok
    ? t.splitBlocked
    : splitOptions.length === 0
      ? t.awsNoSplit
      : null;

  return (
    <CidrSheet title={t.title} closeLabel={t.close} onClose={onClose}>
      <p className="cidr-sheet__hint">{t.hint}</p>
      <p className="cidr-sheet__hint cidr-sheet__hint--strong">{t.cidr(block.cidr)}</p>
      {block.name && <p className="cidr-sheet__hint">{block.name}</p>}
      <button
        type="button"
        className="cidr-sheet__btn cidr-sheet__btn--primary cidr-sheet__btn--full"
        onClick={onEdit}
      >
        {t.edit}
      </button>
      <button
        type="button"
        className="cidr-sheet__btn cidr-sheet__btn--full"
        disabled={!canSplit}
        onClick={onSplit}
      >
        {t.split}
      </button>
      {splitHint && <p className="cidr-sheet__hint">{splitHint}</p>}
    </CidrSheet>
  );
}
